import { useFormContext } from "react-hook-form";

import { Label } from "@fronterahealth/frontera-ui-components";

import { FormInputField } from "@components/AssessmentFormRenderer/components/FormInputField";
import { KeyValueListSection } from "@components/AssessmentFormRenderer/types/layoutTypes";
import { getNestedError } from "@components/AssessmentFormRenderer/utils/getNestedError";

interface FormKeyValueListProps {
  section: KeyValueListSection;
}

/**
 * Renders an array of single-key dicts as label + value pairs.
 * Each item is e.g. {"Ethnicity": "Caucasian"} — the object key becomes
 * the display label and the value is shown as an editable/read-only field.
 */
export const FormKeyValueList: React.FC<FormKeyValueListProps> = ({ section }) => {
  const { watch, formState } = useFormContext();
  const items: Record<string, string>[] = watch(section.source) ?? [];
  const editable = section.editable ?? true;

  return (
    <div className="flex flex-col gap-2">
      <Label displayType="loud">{section.label}</Label>
      {items.map((item, rowIndex) => {
        const entryKey = Object.keys(item)[0];
        if (!entryKey) return null;
        const fieldPath = `${section.source}.${rowIndex}.${entryKey}`;
        const error = getNestedError(formState.errors, fieldPath);

        return (
          <div key={`${section.source}-${rowIndex}`} className="flex flex-row items-center gap-x-4">
            <div className="w-60 shrink-0">
              <Label>{entryKey}</Label>
            </div>
            <div className="w-60 flex-auto">
              <FormInputField fieldPath={fieldPath} editable={editable} error={error} />
            </div>
          </div>
        );
      })}
    </div>
  );
};
